import type { CSSProperties, FC, ReactNode } from 'react'
import type { TileCell } from '@/components/landing/tiles'

import Image from 'next/image'
import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { TrialButton } from '@/components/generals/trial-button'
import { SectionBadge } from '@/components/landing/section-badge'
import { TileField } from '@/components/landing/tiles'
import { useInViewTrigger } from '@/hooks/use-in-view-trigger'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { art } from '@/constants/art'

const TOP_LEFT: TileCell[] = [[0, 0], [2, 0], [3, 0], [1, 1]]
const TOP_RIGHT: TileCell[] = [[3, 0], [1, 1], [2, 2], [3, 2]]

// Fixed positions so the server and the browser place every firefly in the same spot.
const FIREFLIES = [
    { left: 6, top: 62, delay: 0, duration: 7, x: 16, y: -24 },
    { left: 13, top: 78, delay: 2.4, duration: 8, x: -12, y: -18 },
    { left: 19, top: 55, delay: 4.1, duration: 6.5, x: 20, y: -14 },
    { left: 27, top: 84, delay: 1.2, duration: 7.5, x: 10, y: -26 },
    { left: 33, top: 69, delay: 5.3, duration: 8.5, x: -18, y: -20 },
    { left: 41, top: 88, delay: 3, duration: 6.8, x: 14, y: -16 },
    { left: 48, top: 74, delay: 0.8, duration: 7.2, x: -10, y: -28 },
    { left: 56, top: 91, delay: 4.6, duration: 8.2, x: 18, y: -18 },
    { left: 63, top: 80, delay: 2, duration: 6.6, x: -16, y: -22 },
    { left: 71, top: 66, delay: 5.8, duration: 7.8, x: 12, y: -24 },
    { left: 78, top: 86, delay: 1.6, duration: 7, x: -14, y: -16 },
    { left: 85, top: 72, delay: 3.6, duration: 8.4, x: 16, y: -20 },
    { left: 92, top: 83, delay: 0.4, duration: 6.9, x: -12, y: -26 },
    { left: 97, top: 64, delay: 4.9, duration: 7.6, x: -18, y: -14 }
]

// 0 before the scene arrives, 1 at its closest, back to 0 as you scroll on.
const SCENE_ZOOM = '(min(max(var(--cta-progress, 1) - 0.08, 0) / 0.34, 1) - min(max(var(--cta-progress, 1) - 0.46, 0) / 0.22, 1))'

// One copy of the painting so nothing doubles up: it pushes toward you from the foreground and rises slightly.
const SCENE_LAYERS = [
    { depth: 0.24, lift: 40, mask: undefined }
]

// Content inside the card rises in after the card settles.
const CARD_STAGGER = 'group-data-[anim=armed]/cta:opacity-0 motion-safe:group-data-[anim=play]/cta:animate-rise'

export const CtaSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const [cardRef, cardState] = useInViewTrigger<HTMLDivElement>()
    const { nightLandscape } = art

    useScrollProgress(sectionRef, '--cta-progress', 'through')

    return (
        <section
            ref={sectionRef}
            id='get-started'
            aria-labelledby='cta-title'
            className='relative isolate overflow-hidden bg-white pt-16 sm:pt-24'
        >
            <TileField cells={TOP_LEFT} size='76px' className='top-0 left-[6%] max-lg:hidden' />
            <TileField cells={TOP_RIGHT} size='76px' className='top-10 -right-8 max-lg:hidden' />

            <div
                aria-hidden
                className='pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[70%] overflow-hidden'
                style={{ translate: '0 calc((1 - var(--cta-progress, 1)) * 80px)' }}
            >
                {nightLandscape ? (
                    SCENE_LAYERS.map((layer, index) => (
                        <div
                            key={index}
                            className='absolute inset-0 origin-[50%_100%]'
                            style={{
                                scale: `calc(1 + ${layer.depth} * ${SCENE_ZOOM})`,
                                translate: `0 calc(${layer.lift}px * ${SCENE_ZOOM})`,
                                maskImage: layer.mask,
                                WebkitMaskImage: layer.mask
                            }}
                        >
                            <Image
                                src={nightLandscape.src}
                                alt=''
                                fill
                                sizes='100vw'
                                className='fade-top object-cover object-[50%_60%] [--fade:8%]'
                            />
                        </div>
                    ))
                ) : (
                    <div className='fade-top size-full bg-[linear-gradient(180deg,#7d8fd6_0%,#3f53a6_40%,#26367a_70%,#1b2a4f_100%)] [--fade:35%]' />
                )}

                {FIREFLIES.map((fly, index) => (
                    <span
                        key={index}
                        className={cn(
                            'absolute size-[5px] rounded-full bg-[#f5f8d9] opacity-60 shadow-[0_0_10px_3px_rgba(236,244,186,0.65)] motion-safe:animate-firefly',
                            index % 2 === 1 && 'max-sm:hidden'
                        )}
                        style={{
                            left: `${fly.left}%`,
                            top: `${fly.top}%`,
                            animationDelay: `${fly.delay}s`,
                            animationDuration: `${fly.duration}s`,
                            '--fx': `${fly.x}px`,
                            '--fy': `${fly.y}px`
                        } as CSSProperties}
                    />
                ))}
            </div>

            <div className='relative mx-auto w-full max-w-[1000px] px-5'>
                <div
                    ref={cardRef}
                    data-anim={cardState}
                    className='group/cta relative overflow-hidden rounded-[36px] border border-white/80 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.9)_42%,rgba(255,255,255,0.28)_100%)] px-6 pt-14 pb-16 text-center shadow-[0_50px_100px_-60px_rgba(20,30,80,0.7)] backdrop-blur-xl sm:rounded-[48px] sm:px-12 sm:pt-20 sm:pb-24'
                >
                    <div className={CARD_STAGGER} style={{ animationDelay: '250ms' }}>
                        <SectionBadge>Ready when you are</SectionBadge>
                    </div>

                    <h2
                        id='cta-title'
                        className={cn('mt-7 font-heading text-[clamp(2.1rem,3.2vw,2.85rem)] leading-[1.15] tracking-[-0.03em] text-balance text-ink', CARD_STAGGER)}
                        style={{ animationDelay: '400ms' }}
                    >
                        Give your team its hours back.
                    </h2>

                    <p
                        className={cn('mx-auto mt-4 max-w-[52ch] text-[17px] leading-relaxed text-ink/85 sm:text-[19px]', CARD_STAGGER)}
                        style={{ animationDelay: '550ms' }}
                    >
                        Set up your first agent in an afternoon and watch the hours add up from the very first week.
                    </p>

                    <div className={CARD_STAGGER} style={{ animationDelay: '700ms' }}>
                        <TrialButton source='final-cta' className='mt-9' />
                    </div>
                </div>
            </div>

            <div aria-hidden className='h-[clamp(13rem,30vw,24rem)]' />
        </section>
    )
}
